import type { FlatRow } from '@mona-roadmap/domain';

export type NavAction =
  | { type: 'focus'; key: string }
  | { type: 'expand'; key: string }
  | { type: 'collapse'; key: string }
  | { type: 'toggle'; key: string }
  | { type: 'open'; key: string }
  | { type: 'rename'; key: string };

/**
 * Pure keyboard model of the tree grid (WAI-ARIA treegrid pattern). Returns the action to perform
 * for a keypress on the focused row, or null when the key is not handled.
 */
export function navAction(key: string, rows: readonly FlatRow[], focusedKey: string | null): NavAction | null {
  if (rows.length === 0) return null;
  const idx = focusedKey ? rows.findIndex((r) => r.node.key === focusedKey) : -1;
  const at = (i: number): string => rows[Math.max(0, Math.min(rows.length - 1, i))]!.node.key;
  if (idx < 0) {
    return ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key) ? { type: 'focus', key: at(0) } : null;
  }
  const row = rows[idx]!;
  switch (key) {
    case 'ArrowDown':
      return idx < rows.length - 1 ? { type: 'focus', key: at(idx + 1) } : null;
    case 'ArrowUp':
      return idx > 0 ? { type: 'focus', key: at(idx - 1) } : null;
    case 'Home':
      return { type: 'focus', key: at(0) };
    case 'End':
      return { type: 'focus', key: at(rows.length - 1) };
    case 'ArrowRight':
      if (row.hasChildren && !row.expanded) return { type: 'expand', key: row.node.key };
      if (row.hasChildren && row.expanded) {
        const child = rows[idx + 1];
        return child && child.parentKey === row.node.key ? { type: 'focus', key: child.node.key } : null;
      }
      return null;
    case 'ArrowLeft':
      if (row.hasChildren && row.expanded) return { type: 'collapse', key: row.node.key };
      return row.parentKey ? { type: 'focus', key: row.parentKey } : null;
    case ' ':
    case 'Spacebar':
      return row.hasChildren ? { type: 'toggle', key: row.node.key } : null;
    case 'Enter':
      return { type: 'open', key: row.node.key };
    case 'F2':
      return { type: 'rename', key: row.node.key };
    default:
      return null;
  }
}

/** True when the event target is somewhere keystrokes belong to the user (typing, menus, dialogs). */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.closest('[role="menu"], [role="listbox"], [role="dialog"], [role="combobox"]') !== null;
}
